import { TaskRowEnd } from "slot-definitions"
import { IconButton } from "@mui/material"
import { prevent } from "lib/prevent"
import { IsEditMode } from "lib/@components/edit-mode"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { createAudit, deleteJustification } from "lib/@offline/queued-audits"
import { indexTasksInSchedule } from "../lib/index-tasks-in-schedule"
import { SummaryDataChanged, TaskListRefresh } from "event-definitions"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { invalidate } from "lib/graphql/cache"
import { getAuditsByReferenceIdAndType } from "routes/audit/controller/get-audits-by-reference-id"
import { useDialog } from "lib/@hooks/useDialog"
import { ConfirmDialogWithSavedInput } from "lib/@dialogs/confirm-dialog-with-saved-input"
import { PAYLOAD_STATUS_TASK_REMOVAL_UNDONE, PAYLOAD_STATUS_TASK_REMOVED } from "routes/audit/lib/audit-constants"

const AUDIT_EVENT_TASK_REMOVED = "tailoring!custom-schedule-delete-task"

TaskRowEnd.plug(<RemoveButton priority={200} />)

function RemoveButton() {
    const { schedule, task, referenceId, draftId, originalTasks } = useBoundContext()

    const confirmDeleteDialog = useDialog(<ConfirmDialogWithSavedInput />)

    return (
        <IsEditMode>
            <IconButton
                aria-label="Remove task"
                color="secondary"
                onClick={prevent(remove)}
                size="small"
                data-cy="remove-button"
            >
                <Iconify icon="mdi:delete" />
            </IconButton>
        </IsEditMode>
    )

    async function remove() {
        const taskTitle = task?.title
        const deletedTaskId = task?.taskId
        const isHeader = task?.isHeader
        const isNewTask = !isHeader ? !originalTasks.map((t) => t?.taskId).includes(deletedTaskId) : !!task?.newHeader

        if (isNewTask) {
            try {
                const audits = await getAuditsByReferenceIdAndType(referenceId, "customSchedule", {
                    "payload.changeType": "task",
                    "payload.idForDraft": draftId,
                    "payload.taskId": deletedTaskId,
                })
                await Promise.all(audits.map((a) => deleteJustification(a.id)))
            } catch (e) {
                console.warn("Audit cleanup failed (offline or network error):", e?.message || e)
            }
            removeTask(schedule, task, taskTitle)
            return
        }

        const deleteDialogInput = await confirmDeleteDialog({
            title: `Delete Task: ${taskTitle}`,
            message:
                "If you'd like, leave a justification to help others understand why it was removed. You can reverse this with the undo button.",
            label: "Add justification (Optional)",
            dataCy: "justification-input",
            confirmProp: "justification",
        })
        if (deleteDialogInput) {
            const { justification } = deleteDialogInput

            try {
                await deleteSupersededAudits(deletedTaskId)
            } catch (e) {
                console.warn("Outdated audit deletion failed (offline or network error):", e?.message || e)
            }

            try {
                await createAudit("", justification || "", "customSchedule", referenceId, {
                    changeType: "task",
                    idForDraft: draftId,
                    taskId: deletedTaskId,
                    taskTitle: taskTitle,
                    isHeader: task?.isHeader,
                    status: PAYLOAD_STATUS_TASK_REMOVED,
                    event: AUDIT_EVENT_TASK_REMOVED,
                    subHeader: "removed from schedule",
                    ...(justification ? { reason: justification } : {}),
                })
            } catch (e) {
                console.warn("Audit creation failed (offline or network error):", e?.message || e)
            }
            removeTask(schedule, task, taskTitle)
        }
    }

    // deletes all audits for a task that has been deleted and then redo-ed back into existence n number of times.
    // keeps the justification log clear of duplicate deletion audits
    async function deleteSupersededAudits(taskId) {
        const superseded = await getAuditsByReferenceIdAndType(referenceId, "customSchedule", {
            "payload.changeType": "task",
            "payload.idForDraft": draftId,
            "payload.taskId": taskId,
            "payload.status": PAYLOAD_STATUS_TASK_REMOVAL_UNDONE,
        })
        await Promise.all(superseded.map((a) => deleteJustification(a.id)))
    }

    function removeTask(schedule, task, taskTitle) {
        schedule.tasks.splice(task.$sort, 1)
        indexTasksInSchedule(schedule)
        TaskListRefresh.raise()
        SummaryDataChanged.raiseOnce()
        infoSnackbar(`Task: ${taskTitle} deleted`)
        invalidate("getAuditsByReferenceIdAndType")
    }
}
